import React, { useState, useRef, ReactNode } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { styled } from '@mui/material/styles';
import { useDataProvider, useNotify } from 'react-admin';

interface DragDropFileUploadProps {
  // File handling
  onUploadSuccess?: (fileData: any) => void;
  onUploadError?: (error: string) => void;
  acceptTypes?: string;
  maxSizeMB?: number;
  multiple?: boolean;
  
  // Customization
  title?: string;
  subtitle?: string;
  buttonText?: string;
  dragText?: string;
  dragSubtext?: string;
  required?: boolean;
  disabled?: boolean;
  
  // Upload configuration
  folderPath?: string;
  description?: string;
  
  // Visual customization
  height?: number;
  renderSelectedFile?: (file: File, onCancel: () => void, onUpload: () => void) => ReactNode;
  renderUploading?: (file: File, progress: number) => ReactNode;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const DragDropFileUpload: React.FC<DragDropFileUploadProps> = ({
  onUploadSuccess = () => {},
  onUploadError = () => {},
  acceptTypes = '*/*',
  maxSizeMB = 10,
  multiple = false,
  title = 'Drag & drop or click to upload',
  subtitle = 'Choose a file or drag it here',
  buttonText = 'Browse Files',
  dragText = 'Drop file here to upload',
  dragSubtext = 'Release to upload your file',
  required = false,
  disabled = false,
  folderPath = 'uploads',
  description = 'Uploaded file',
  height = 120,
  renderSelectedFile,
  renderUploading,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const validateFiles = (files: FileList | File[]): File[] => {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    
    for (const file of fileArray) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSizeMB) {
        const errorMsg = `File "${file.name}" exceeds ${maxSizeMB}MB limit`;
        setError(errorMsg);
        onUploadError(errorMsg);
        continue;
      }
      validFiles.push(file);
    }
    
    return validFiles;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setError('');
    const validFiles = validateFiles(files);
    if (validFiles.length > 0) {
      setSelectedFiles(multiple ? validFiles : [validFiles[0]]);
    }
  };

  // Drag and drop event handlers
  const handleDragEnter = (event: React.DragEvent) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDrop = (event: React.DragEvent) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      setError('');
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles(multiple ? validFiles : [validFiles[0]]);
      }
    }
  };

  const openFileExplorer = () => {
    if (disabled) return;
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleUpload = async (files = selectedFiles) => {
    if (files.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const uploadPromises = files.map(async (file) => {
        // Simulate progress
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            const next = prev + 10;
            return next > 90 ? 90 : next;
          });
        }, 200);

        try {
          const result = await dataProvider.create('files', {
            data: {
              file,
              name: file.name,
              folderPath,
              description: description || file.name,
              isPublic: false,
            },
          });

          clearInterval(progressInterval);
          return result.data;
        } catch (error) {
          clearInterval(progressInterval);
          throw error;
        }
      });

      const results = await Promise.all(uploadPromises);
      setUploadProgress(100);

      // Success
      if (multiple) {
        onUploadSuccess(results);
      } else {
        onUploadSuccess(results[0]);
      }
      
      notify(`${files.length} file${files.length > 1 ? 's' : ''} uploaded successfully`, { type: 'success' });
      
      // Reset after success
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setSelectedFiles([]);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }, 1000);

    } catch (error: any) {
      console.error('File upload failed:', error);
      const errorMsg = error.message || 'Upload failed';
      setError(errorMsg);
      onUploadError(errorMsg);
      setUploading(false);
      setUploadProgress(0);
      notify('Upload failed: ' + errorMsg, { type: 'error' });
    }
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    setError('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  // Show upload progress
  if (uploading && selectedFiles.length > 0) {
    if (renderUploading) {
      return renderUploading(selectedFiles[0], uploadProgress) as React.ReactElement;
    }

    return (
      <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
        <CircularProgress variant="determinate" value={uploadProgress} size={60} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Uploading {selectedFiles[0]?.name}...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {uploadProgress}% Complete
        </Typography>
      </Paper>
    );
  }

  // Show file selected state
  if (selectedFiles.length > 0) {
    if (renderSelectedFile) {
      return renderSelectedFile(selectedFiles[0], handleCancel, () => handleUpload()) as React.ReactElement;
    }

    return (
      <Paper sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
        {selectedFiles.map((file, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: index < selectedFiles.length - 1 ? 2 : 0 }}>
            <InsertDriveFileIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1">
                {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Size: {(file.size / (1024 * 1024)).toFixed(2)} MB
              </Typography>
            </Box>
          </Box>
        ))}

        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => handleUpload()} disabled={!!error}>
            Upload {selectedFiles.length > 1 ? `${selectedFiles.length} Files` : 'File'}
          </Button>
        </Box>
      </Paper>
    );
  }

  // Show initial upload area with drag-and-drop
  return (
    <Box>
      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileExplorer}
        sx={{ 
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          '&:hover': disabled ? {} : {
            '& .upload-area': {
              borderColor: 'primary.main',
              backgroundColor: '#f0f7ff',
            }
          }
        }}
      >
        <VisuallyHiddenInput 
          type="file" 
          accept={acceptTypes} 
          onChange={handleFileChange} 
          ref={inputRef}
          multiple={multiple}
          disabled={disabled}
        />
        
        <Paper 
          className="upload-area"
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            border: dragActive ? '2px solid' : '2px dashed',
            borderColor: dragActive ? 'primary.main' : '#ccc',
            backgroundColor: dragActive ? '#e3f2fd' : '#fafafa',
            transition: 'all 0.2s ease-in-out',
            minHeight: height,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {dragActive ? (
            <>
              <FileUploadIcon 
                sx={{ 
                  fontSize: 48, 
                  color: 'primary.main', 
                  mb: 1,
                  animation: 'pulse 1.5s ease-in-out infinite'
                }} 
              />
              <Typography variant="h6" color="primary.main" sx={{ mb: 1 }}>
                {dragText}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dragSubtext}
              </Typography>
            </>
          ) : (
            <>
              <CloudUploadIcon 
                sx={{ 
                  fontSize: 48, 
                  color: 'text.secondary', 
                  mb: 2 
                }} 
              />
              <Typography variant="h6" sx={{ mb: 1 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {subtitle}
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                startIcon={<CloudUploadIcon />}
                disabled={disabled}
                sx={{ 
                  mb: 2,
                  minWidth: '200px',
                  minHeight: '44px', // Touch-friendly size
                }}
              >
                {buttonText}
              </Button>
              
              <Typography variant="caption" display="block" color="text.secondary">
                Max file size: {maxSizeMB}MB
              </Typography>
              
              {acceptTypes !== '*/*' && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Accepted formats: {acceptTypes}
                </Typography>
              )}
              
              {multiple && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Multiple files allowed
                </Typography>
              )}
              
              {required && (
                <Typography variant="caption" display="block" color="error" sx={{ mt: 1 }}>
                  File upload is required
                </Typography>
              )}
            </>
          )}
        </Paper>

        {/* Drag overlay */}
        {dragActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Add keyframe animation for pulse effect */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </Box>
  );
};